import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const PALIERS = [
  { label: 'Tranche 1 (0–500k)', key: 'taux_commission_1', max: 500000 },
  { label: 'Tranche 2 (500k–1.5M)', key: 'taux_commission_2', max: 1500000 },
  { label: 'Tranche 3 (1.5M–3M)', key: 'taux_commission_3', max: 3000000 },
  { label: 'Tranche 4 (>3M)', key: 'taux_commission_4', max: null },
];

const VISIBILITE_OPTIONS = [
  { value: 'masque', label: '🔒 Masqué', desc: 'La commerciale ne voit rien' },
  { value: 'resume', label: '👁️ Résumé', desc: 'Total à percevoir ce mois' },
  { value: 'detail', label: '📊 Détail', desc: 'Commissions par devis + historique' },
];

const CommissionsManager = () => {
  const [commerciales, setCommerciales] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [moisSelectionne, setMoisSelectionne] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [{ data: profiles }, { data: comms }, { data: qs }] = await Promise.all([
      supabase.from('profiles').select('*').eq('role', 'commerciale').order('nom'),
      supabase.from('commissions').select('*').order('created_at', { ascending: false }),
      supabase.from('quotes').select('id, user_id, total_with_vat, statut, created_at, quote_number').order('created_at', { ascending: false }),
    ]);
    setCommerciales(profiles || []);
    setCommissions(comms || []);
    setQuotes(qs || []);
    setLoading(false);
  };

  const calculerCommission = (ca, taux1, taux2, taux3, taux4) => {
    let commission = 0;
    if (ca <= 0) return 0;
    const t1 = Math.min(ca, 500000);
    commission += t1 * (taux1 / 100);
    if (ca > 500000) {
      const t2 = Math.min(ca - 500000, 1000000);
      commission += t2 * (taux2 / 100);
    }
    if (ca > 1500000) {
      const t3 = Math.min(ca - 1500000, 1500000);
      commission += t3 * (taux3 / 100);
    }
    if (ca > 3000000) {
      const t4 = ca - 3000000;
      commission += t4 * (taux4 / 100);
    }
    return Math.round(commission);
  };

  const calculerFixe = (nbDevis, fixeRef) => {
    if (nbDevis === 0) return Math.round(fixeRef * 0.70);
    if (nbDevis === 1) return Math.round(fixeRef * 0.85);
    return fixeRef;
  };

  const sauvegarderProfil = async (id, champs) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    await supabase.from('profiles').update(champs).eq('id', id);
    setSaving(prev => ({ ...prev, [id]: false }));
    setSaved(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [id]: false })), 2000);
    setCommerciales(prev => prev.map(c => c.id === id ? { ...c, ...champs } : c));
  };

  const marquerVersee = async (commId) => {
    await supabase.from('commissions').update({
      statut: 'versee',
      date_versement: new Date().toISOString().split('T')[0],
    }).eq('id', commId);
    loadData();
  };

  if (loading) return <div style={{ padding: '20px', color: '#6b7280' }}>Chargement...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* ── Section 1 : Paramètres par commerciale ── */}
      <div>
        <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e40af', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          👥 Paramètres équipe commerciale
        </div>
        {commerciales.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: '13px', padding: '20px', background: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
            Aucune commerciale trouvée dans les profils.
          </div>
        )}
        {commerciales.map(c => {
          const myQuotesMois = quotes.filter(q => q.user_id === c.id && q.created_at?.startsWith(moisSelectionne));
          const myQuotesSignes = myQuotesMois.length;
          const caInstallePaye = quotes.filter(q => q.user_id === c.id && q.statut === 'installe_paye').reduce((s, q) => s + (q.total_with_vat || 0), 0);
          const fixeApplicable = calculerFixe(myQuotesSignes, c.fixe_reference || 140000);
          const commissionDue = calculerCommission(caInstallePaye, c.taux_commission_1 || 1.5, c.taux_commission_2 || 2.0, c.taux_commission_3 || 2.5, c.taux_commission_4 || 3.0);

          return (
            <div key={c.id} style={{ background: '#f8faff', borderRadius: '12px', padding: '20px', border: '1px solid #e0e7ff', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{c.nom}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{c.email} · {c.mobile || '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ textAlign: 'center', background: 'white', borderRadius: '8px', padding: '8px 14px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e40af' }}>{myQuotesSignes}</div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>devis ce mois</div>
                  </div>
                  <div style={{ textAlign: 'center', background: 'white', borderRadius: '8px', padding: '8px 14px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#059669' }}>{fmt(fixeApplicable)}</div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>fixe applicable</div>
                  </div>
                  <div style={{ textAlign: 'center', background: 'white', borderRadius: '8px', padding: '8px 14px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#f59e0b' }}>{fmt(commissionDue)}</div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>commission due</div>
                  </div>
                  <div style={{ textAlign: 'center', background: '#1e40af', borderRadius: '8px', padding: '8px 14px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'white' }}>{fmt(fixeApplicable + commissionDue)}</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>total à verser</div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {/* Fixe référence */}
                <div style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>FIXE DE RÉFÉRENCE</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input type="number" defaultValue={c.fixe_reference || 140000} min="0" step="5000"
                      id={`fixe-${c.id}`}
                      style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 8px', fontSize: '13px', fontWeight: 600 }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>FCFA</span>
                    <button onClick={() => sauvegarderProfil(c.id, { fixe_reference: parseInt(document.getElementById(`fixe-${c.id}`).value) })}
                      style={{ background: saved[c.id] ? '#10b981' : '#1e40af', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', cursor: 'pointer' }}>
                      {saving[c.id] ? '...' : saved[c.id] ? '✓' : 'OK'}
                    </button>
                  </div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
                    0 devis → {fmt(Math.round((c.fixe_reference||140000)*0.7))} · 1 devis → {fmt(Math.round((c.fixe_reference||140000)*0.85))} · 2+ → {fmt(c.fixe_reference||140000)}
                  </div>
                </div>

                {/* Taux commissions */}
                <div style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>TAUX COMMISSIONS (%)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                    {PALIERS.map((p, idx) => (
                      <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '9px', color: '#9ca3af', minWidth: '20px' }}>T{idx+1}</span>
                        <input type="number" defaultValue={c[p.key] || [1.5,2,2.5,3][idx]} min="0" max="10" step="0.1" id={`${p.key}-${c.id}`}
                          style={{ width: '46px', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', fontWeight: 600 }} />
                        <span style={{ fontSize: '9px', color: '#9ca3af' }}>%</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => sauvegarderProfil(c.id, {
                    taux_commission_1: parseFloat(document.getElementById(`taux_commission_1-${c.id}`).value),
                    taux_commission_2: parseFloat(document.getElementById(`taux_commission_2-${c.id}`).value),
                    taux_commission_3: parseFloat(document.getElementById(`taux_commission_3-${c.id}`).value),
                    taux_commission_4: parseFloat(document.getElementById(`taux_commission_4-${c.id}`).value),
                  })}
                    style={{ marginTop: '8px', width: '100%', background: '#1e40af', color: 'white', border: 'none', borderRadius: '6px', padding: '5px', fontSize: '11px', cursor: 'pointer' }}>
                    Sauvegarder taux
                  </button>
                </div>

                {/* Visibilité */}
                <div style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>VISIBILITÉ COMMISSIONS</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {VISIBILITE_OPTIONS.map(opt => (
                      <button key={opt.value}
                        onClick={() => sauvegarderProfil(c.id, { visibilite_commissions: opt.value })}
                        style={{
                          padding: '6px 10px', borderRadius: '6px', border: '1px solid',
                          borderColor: c.visibilite_commissions === opt.value ? '#1e40af' : '#e5e7eb',
                          background: c.visibilite_commissions === opt.value ? '#eff6ff' : 'white',
                          color: c.visibilite_commissions === opt.value ? '#1e40af' : '#6b7280',
                          fontSize: '11px', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                        }}>
                        {opt.label}
                        <span style={{ fontSize: '9px', fontWeight: 400, marginLeft: '4px', color: '#9ca3af' }}>{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Section 2 : Suivi commissions ── */}
      <div>
        <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
          <span>💰 Suivi commissions</span>
          <input type="month" value={moisSelectionne} onChange={e => setMoisSelectionne(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 8px', fontSize: '12px' }} />
        </div>

        {commissions.filter(c => c.mois === moisSelectionne).length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: '13px', padding: '20px', background: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
            Aucune commission enregistrée pour ce mois.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {commissions.filter(c => c.mois === moisSelectionne).map(comm => {
              const profil = commerciales.find(c => c.id === comm.user_id);
              return (
                <div key={comm.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e5e7eb' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>{profil?.nom || '—'}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Devis #{comm.quote_id} · CA installé+payé : {fmt(comm.ca_installe_paye)} FCFA</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#f59e0b' }}>{fmt(comm.commission_calculee)} FCFA</div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>commission</div>
                  </div>
                  <div style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                    background: comm.statut === 'versee' ? '#d1fae5' : comm.statut === 'a_verser' ? '#fef3c7' : '#f3f4f6',
                    color: comm.statut === 'versee' ? '#065f46' : comm.statut === 'a_verser' ? '#92400e' : '#6b7280' }}>
                    {comm.statut === 'versee' ? '✅ Versée' : comm.statut === 'a_verser' ? '🟡 À verser' : '⏳ En attente'}
                  </div>
                  {comm.statut === 'a_verser' && (
                    <button onClick={() => marquerVersee(comm.id)}
                      style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      Marquer versée
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionsManager;
